// Slice 40 — first-run bootstrap. Single shot ever: the backend returns 409
// the second time, but the SPA boot route also branches on `/admin/session`
// so this screen never re-renders for a returning operator.
//
// Honours spec §2 password rules (≥12 chars) and the Argon2id-pinned hash:
// the backend is the source of truth on validity, the SPA just front-loads
// the constraints with hint copy + a confirm-password field.

import { useState } from "react";
import { Api } from "../api/client";
import { HqButton, HqCard, HqInput, Icons, Logo } from "../ui";

export interface SetupScreenProps {
  onBootstrapped: (username: string) => void;
}

export function SetupScreen({ onBootstrapped }: SetupScreenProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    if (password.length < 12) {
      setErr("Password must be at least 12 characters.");
      return;
    }
    setBusy(true);
    const res = await Api.bootstrap(username.trim().toLowerCase(), password);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message || "Bootstrap failed.");
      return;
    }
    onBootstrapped(res.data.username);
  }

  return (
    <AuthShell>
      <HqCard
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Icons.Lock width={14} height={14} sw={1.7} style={{ color: "var(--hq-accent)" }} />
            First-time setup
          </span>
        }
      >
        <p
          style={{
            margin: "0 0 18px",
            color: "var(--hq-text-muted)",
            fontSize: 12.5,
            lineHeight: 1.55,
          }}
        >
          Choose the superadmin credentials. This runs <strong>once</strong>: the account is locked
          after creation and the only way to add a second operator is the staff-invite flow (Slice
          47). Password is hashed with Argon2id; pick something long.
        </p>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label="Username">
            <HqInput
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="stian"
              hint="2–32 chars, [a-z0-9._-], lowercased on submit"
              required
            />
          </Field>
          <Field label="Password">
            <HqInput
              autoComplete="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint="Minimum 12 characters"
              required
            />
          </Field>
          <Field label="Confirm password">
            <HqInput
              autoComplete="new-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </Field>
          {err && (
            <div
              style={{
                padding: "8px 12px",
                borderRadius: "var(--hq-radius-md)",
                background: "var(--hq-danger-soft)",
                color: "var(--hq-danger)",
                fontSize: 12,
                border: "1px solid rgba(251,113,133,.25)",
              }}
            >
              {err}
            </div>
          )}
          <HqButton
            type="submit"
            variant="primary"
            size="lg"
            loading={busy}
            style={{ marginTop: 4 }}
          >
            Create superadmin & sign in
          </HqButton>
        </form>
      </HqCard>
    </AuthShell>
  );
}

export function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        height: "100%",
        background: "var(--hq-bg)",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <div
        style={{ width: "100%", maxWidth: 440, display: "flex", flexDirection: "column", gap: 18 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Logo size={28} />
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Vyvern</span>
            <span
              className="hq-mono"
              style={{
                fontSize: 10,
                color: "var(--hq-text-dim)",
                letterSpacing: ".18em",
                textTransform: "uppercase",
              }}
            >
              back-office · staff
            </span>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 11.5, color: "var(--hq-text-muted)" }}>{label}</span>
      {children}
    </label>
  );
}
